import { observer } from "mobx-react-lite";
import { useCallback } from "react";

import { DateTime } from "@bps/date-time";
import { NoDataTile, useTheme } from "@bps/fluent-ui";
import { PagingOptions } from "@libs/api/dtos/index.ts";
import {
  AuditLogEntityType,
  AuditLogOperationType,
  AuditLogsDto
} from "@libs/gateways/user-experience/UserExperienceGateway.dtos.ts";
import { nameof } from "@libs/utils/name-of.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";
import { InfiniteScrollList } from "@ui-components/InfiniteScrollList/InfiniteScrollList.tsx";
import { InfiniteScrollListColumn } from "@ui-components/InfiniteScrollList/InfiniteScrollList.types.ts";

interface AuditLogCardProps {
  patientId: string;
}

const auditLogColumns = (
  patientLabelCapitalised: string
): InfiniteScrollListColumn[] => [
  {
    name: "Date",
    key: nameof<AuditLogsDto>("performedAt"),
    onRender: (item: AuditLogsDto) =>
      DateTime.fromISO(item.performedAt).toDayDefaultFormat(),
    minWidth: 60,
    maxWidth: 80
  },
  {
    name: "Action",
    key: nameof<AuditLogsDto>("operationType"),
    minWidth: 120,
    maxWidth: 200,
    onRender: (item: AuditLogsDto) =>
      item.operationType === AuditLogOperationType.exportPatientRecord
        ? `${patientLabelCapitalised} record export`
        : null
  },
  {
    name: "User",
    key: nameof<AuditLogsDto>("performedBy"),
    minWidth: 100,
    onRender: (item: AuditLogsDto) =>
      item.operationType === AuditLogOperationType.exportPatientRecord
        ? item.properties?.find(o => o.name === "ProviderName")?.value
        : null
  }
];

export const AuditLogCard: React.FC<AuditLogCardProps> = observer(
  ({ patientId }) => {
    const { userExperience } = useStores();
    const theme = useTheme();
    const patientLabelCapitalised = usePatientLabel(true);

    const fetchAuditLogs = useCallback(
      async (query?: PagingOptions) =>
        await userExperience.getAuditLogs({
          ...query,
          entityId: patientId,
          entityType: AuditLogEntityType.patient
        }),
      [patientId, userExperience]
    );

    return (
      <InfiniteScrollList
        refreshKey={userExperience.auditLogListRefreshKey}
        setKey="audit-log-list"
        columns={auditLogColumns(patientLabelCapitalised)}
        getItems={fetchAuditLogs}
        stickyHeader={false}
        detailsListStyles={{
          root: {
            '> div[role="grid"]': {
              position: "relative",
              maxHeight: 250,
              overflowY: "auto",
              overflowX: "hidden"
            }
          },
          headerWrapper: {
            position: "sticky",
            top: 0,
            background: theme.palette.white,
            zIndex: 1
          }
        }}
        onRenderNoResults={() => (
          <NoDataTile
            textProps={{ text: "No items recorded" }}
            linkProps={{ hidden: true }}
            showBoxShadow={false}
          />
        )}
      />
    );
  }
);
